import { useClient } from "@/composables/useClient";
import { deserializeCbiDto, serializeCbiDto, encodeRequestParam } from "../serialization/CbiDateCodec";
import type { CatalogApi } from "../../api/CatalogApi";
import type { AgreementCategoryFetchOptions } from "../../generated/dto/AgreementCategoryFetchOptions";
import type { AgreementCategoryStaff } from "../../generated/dto/AgreementCategoryStaff";
import type { AgreementFetchOptions } from "../../generated/dto/AgreementFetchOptions";
import type { AgreementInstance } from "../../generated/dto/AgreementInstance";
import type { AgreementInstanceFetchOptions } from "../../generated/dto/AgreementInstanceFetchOptions";
import type { AgreementInstanceSearchParams } from "../../generated/dto/AgreementInstanceSearchParams";
import type { AgreementInstanceStaff } from "../../generated/dto/AgreementInstanceStaff";
import type { AgreementInstanceStaffSearchResult } from "../../generated/dto/AgreementInstanceStaffSearchResult";
import type { AgreementRef } from "../../generated/dto/AgreementRef";
import type { AgreementSearchParams } from "../../generated/dto/AgreementSearchParams";
import type { AgreementStaff } from "../../generated/dto/AgreementStaff";
import type { AgreementStaffSearchResult } from "../../generated/dto/AgreementStaffSearchResult";
import type { AgreementVersionChangeRequest } from "../../generated/dto/AgreementVersionChangeRequest";
import type { CatalogDiscountFetchOptions } from "../../generated/dto/CatalogDiscountFetchOptions";
import type { CatalogDiscountRef } from "../../generated/dto/CatalogDiscountRef";
import type { CatalogDiscountStaff } from "../../generated/dto/CatalogDiscountStaff";
import type { CatalogFetchOptions } from "../../generated/dto/CatalogFetchOptions";
import type { CatalogItemAgreementFetchOptions } from "../../generated/dto/CatalogItemAgreementFetchOptions";
import type { CatalogItemAgreementStaff } from "../../generated/dto/CatalogItemAgreementStaff";
import type { CatalogItemAgreementsStaffAssignmentInput } from "../../generated/dto/CatalogItemAgreementsStaffAssignmentInput";
import type { CatalogItemCategoriesStaffAssignmentInput } from "../../generated/dto/CatalogItemCategoriesStaffAssignmentInput";
import type { CatalogItemCategoryFetchOptions } from "../../generated/dto/CatalogItemCategoryFetchOptions";
import type { CatalogItemCategoryStaff } from "../../generated/dto/CatalogItemCategoryStaff";
import type { CatalogItemDiscountFetchOptions } from "../../generated/dto/CatalogItemDiscountFetchOptions";
import type { CatalogItemDiscountStaff } from "../../generated/dto/CatalogItemDiscountStaff";
import type { CatalogItemFetchOptions } from "../../generated/dto/CatalogItemFetchOptions";
import type { CatalogItemInstance } from "../../generated/dto/CatalogItemInstance";
import type { CatalogItemInstanceFetchOptions } from "../../generated/dto/CatalogItemInstanceFetchOptions";
import type { CatalogItemInstanceMember } from "../../generated/dto/CatalogItemInstanceMember";
import type { CatalogItemInstanceStaff } from "../../generated/dto/CatalogItemInstanceStaff";
import type { CatalogItemItemDiscountsStaffAssignmentInput } from "../../generated/dto/CatalogItemItemDiscountsStaffAssignmentInput";
import type { CatalogItemRef } from "../../generated/dto/CatalogItemRef";
import type { CatalogItemSearchParams } from "../../generated/dto/CatalogItemSearchParams";
import type { CatalogItemStaff } from "../../generated/dto/CatalogItemStaff";
import type { CatalogItemStaffSearchResult } from "../../generated/dto/CatalogItemStaffSearchResult";
import type { CatalogItemTaxFetchOptions } from "../../generated/dto/CatalogItemTaxFetchOptions";
import type { CatalogItemTaxStaff } from "../../generated/dto/CatalogItemTaxStaff";
import type { CatalogItemTaxesStaffAssignmentInput } from "../../generated/dto/CatalogItemTaxesStaffAssignmentInput";
import type { CatalogPriceChangeRequest } from "../../generated/dto/CatalogPriceChangeRequest";
import type { CatalogSearchParams } from "../../generated/dto/CatalogSearchParams";
import type { CatalogStaff } from "../../generated/dto/CatalogStaff";
import type { CatalogStaffSearchResult } from "../../generated/dto/CatalogStaffSearchResult";
import type { CatalogTaxFetchOptions } from "../../generated/dto/CatalogTaxFetchOptions";
import type { CatalogTaxRef } from "../../generated/dto/CatalogTaxRef";
import type { CatalogTaxStaff } from "../../generated/dto/CatalogTaxStaff";
import type { CategoryFetchOptions } from "../../generated/dto/CategoryFetchOptions";
import type { CategoryMember } from "../../generated/dto/CategoryMember";
import type { CategoryMemberFetchOptions } from "../../generated/dto/CategoryMemberFetchOptions";
import type { CategoryRef } from "../../generated/dto/CategoryRef";
import type { CategorySearchParams } from "../../generated/dto/CategorySearchParams";
import type { CategoryStaff } from "../../generated/dto/CategoryStaff";
import type { CategoryStaffSearchResult } from "../../generated/dto/CategoryStaffSearchResult";
import type { CreateAgreement } from "../../generated/dto/CreateAgreement";
import type { CreateAgreementCategory } from "../../generated/dto/CreateAgreementCategory";
import type { CreateAgreementInstance } from "../../generated/dto/CreateAgreementInstance";
import type { CreateCatalog } from "../../generated/dto/CreateCatalog";
import type { CreateCatalogItemAgreement } from "../../generated/dto/CreateCatalogItemAgreement";
import type { CreateCatalogItemCategory } from "../../generated/dto/CreateCatalogItemCategory";
import type { CreateCatalogItemInstance } from "../../generated/dto/CreateCatalogItemInstance";
import type { CreateCatalogItemStaff } from "../../generated/dto/CreateCatalogItemStaff";
import type { CreateCategory } from "../../generated/dto/CreateCategory";
import type { CreateProduct } from "../../generated/dto/CreateProduct";
import type { DeleteAgreementCategoryStaff } from "../../generated/dto/DeleteAgreementCategoryStaff";
import type { DeleteAgreementInstanceStaff } from "../../generated/dto/DeleteAgreementInstanceStaff";
import type { DeleteAgreementStaff } from "../../generated/dto/DeleteAgreementStaff";
import type { DeleteCatalogItemAgreementStaff } from "../../generated/dto/DeleteCatalogItemAgreementStaff";
import type { DeleteCatalogItemCategoryStaff } from "../../generated/dto/DeleteCatalogItemCategoryStaff";
import type { DeleteCatalogItemInstanceStaff } from "../../generated/dto/DeleteCatalogItemInstanceStaff";
import type { DeleteCatalogItemStaff } from "../../generated/dto/DeleteCatalogItemStaff";
import type { DeleteCatalogStaff } from "../../generated/dto/DeleteCatalogStaff";
import type { DeleteCategoryStaff } from "../../generated/dto/DeleteCategoryStaff";
import type { DeleteProductStaff } from "../../generated/dto/DeleteProductStaff";
import type { ProductFetchOptions } from "../../generated/dto/ProductFetchOptions";
import type { ProductSearchParams } from "../../generated/dto/ProductSearchParams";
import type { ProductStaff } from "../../generated/dto/ProductStaff";
import type { ProductStaffSearchResult } from "../../generated/dto/ProductStaffSearchResult";
import type { SaveCatalogItemRequest } from "../../generated/dto/SaveCatalogItemRequest";
import type { StockChangeFetchOptions } from "../../generated/dto/StockChangeFetchOptions";
import type { StockChangeStaff } from "../../generated/dto/StockChangeStaff";
import type { UpdateAgreement } from "../../generated/dto/UpdateAgreement";
import type { UpdateAgreementCategory } from "../../generated/dto/UpdateAgreementCategory";
import type { UpdateAgreementInstance } from "../../generated/dto/UpdateAgreementInstance";
import type { UpdateCatalog } from "../../generated/dto/UpdateCatalog";
import type { UpdateCatalogItemAgreement } from "../../generated/dto/UpdateCatalogItemAgreement";
import type { UpdateCatalogItemCategory } from "../../generated/dto/UpdateCatalogItemCategory";
import type { UpdateCatalogItemInstance } from "../../generated/dto/UpdateCatalogItemInstance";
import type { UpdateCatalogItemStaff } from "../../generated/dto/UpdateCatalogItemStaff";
import type { UpdateCategory } from "../../generated/dto/UpdateCategory";
import type { UpdateProduct } from "../../generated/dto/UpdateProduct";
import type { PaginationResult } from "../../generated/dto/PaginationResult";

export class CatalogApiClient implements CatalogApi {
  constructor(private readonly client = useClient()) {}

  async getCategoryMember(id: number, options?: CategoryMemberFetchOptions): Promise<CategoryMember> {
    const { data } = await this.client.get<unknown>(`/category/member/${id}`, { params: serializeCbiDto(options, "generated.dto.CategoryMemberFetchOptions") });
    return deserializeCbiDto<CategoryMember>(data, "generated.dto.CategoryMember");
  }

  async getCategoryStaff(id: number, options?: CategoryFetchOptions): Promise<CategoryStaff> {
    const { data } = await this.client.get<unknown>(`/category/staff/${id}`, { params: serializeCbiDto(options, "generated.dto.CategoryFetchOptions") });
    return deserializeCbiDto<CategoryStaff>(data, "generated.dto.CategoryStaff");
  }

  async getAgreementStaff(id: number, options?: AgreementFetchOptions): Promise<AgreementStaff> {
    const { data } = await this.client.get<unknown>(`/agreement/staff/${id}`, { params: serializeCbiDto(options, "generated.dto.AgreementFetchOptions") });
    return deserializeCbiDto<AgreementStaff>(data, "generated.dto.AgreementStaff");
  }

  async getAgreementInstanceStaff(id: number, options?: AgreementInstanceFetchOptions): Promise<AgreementInstanceStaff> {
    const { data } = await this.client.get<unknown>(`/agreementinstance/staff/${id}`, { params: serializeCbiDto(options, "generated.dto.AgreementInstanceFetchOptions") });
    return deserializeCbiDto<AgreementInstanceStaff>(data, "generated.dto.AgreementInstanceStaff");
  }

  async getAgreementCategoryStaff(id: number, options?: AgreementCategoryFetchOptions): Promise<AgreementCategoryStaff> {
    const { data } = await this.client.get<unknown>(`/agreementcategory/staff/${id}`, { params: serializeCbiDto(options, "generated.dto.AgreementCategoryFetchOptions") });
    return deserializeCbiDto<AgreementCategoryStaff>(data, "generated.dto.AgreementCategoryStaff");
  }

  async getCatalogStaff(id: number, options?: CatalogFetchOptions): Promise<CatalogStaff> {
    const { data } = await this.client.get<unknown>(`/catalog/staff/${id}`, { params: serializeCbiDto(options, "generated.dto.CatalogFetchOptions") });
    return deserializeCbiDto<CatalogStaff>(data, "generated.dto.CatalogStaff");
  }

  async getProductStaff(id: number, options?: ProductFetchOptions): Promise<ProductStaff> {
    const { data } = await this.client.get<unknown>(`/product/staff/${id}`, { params: serializeCbiDto(options, "generated.dto.ProductFetchOptions") });
    return deserializeCbiDto<ProductStaff>(data, "generated.dto.ProductStaff");
  }

  async getCatalogItemStaff(id: number, options?: CatalogItemFetchOptions): Promise<CatalogItemStaff> {
    const { data } = await this.client.get<unknown>(`/catalogitem/staff/${id}`, { params: serializeCbiDto(options, "generated.dto.CatalogItemFetchOptions") });
    return deserializeCbiDto<CatalogItemStaff>(data, "generated.dto.CatalogItemStaff");
  }

  async getCatalogItemInstanceStaff(id: number, options?: CatalogItemInstanceFetchOptions): Promise<CatalogItemInstanceStaff> {
    const { data } = await this.client.get<unknown>(`/catalogiteminstance/staff/${id}`, { params: serializeCbiDto(options, "generated.dto.CatalogItemInstanceFetchOptions") });
    return deserializeCbiDto<CatalogItemInstanceStaff>(data, "generated.dto.CatalogItemInstanceStaff");
  }

  async getCatalogItemCategoryStaff(id: number, options?: CatalogItemCategoryFetchOptions): Promise<CatalogItemCategoryStaff> {
    const { data } = await this.client.get<unknown>(`/catalogitemcategory/staff/${id}`, { params: serializeCbiDto(options, "generated.dto.CatalogItemCategoryFetchOptions") });
    return deserializeCbiDto<CatalogItemCategoryStaff>(data, "generated.dto.CatalogItemCategoryStaff");
  }

  async getCatalogItemAgreementStaff(id: number, options?: CatalogItemAgreementFetchOptions): Promise<CatalogItemAgreementStaff> {
    const { data } = await this.client.get<unknown>(`/catalogitemagreement/staff/${id}`, { params: serializeCbiDto(options, "generated.dto.CatalogItemAgreementFetchOptions") });
    return deserializeCbiDto<CatalogItemAgreementStaff>(data, "generated.dto.CatalogItemAgreementStaff");
  }

  async getAllCategoryMembers(options?: CategoryMemberFetchOptions): Promise<Array<CategoryMember>> {
    const { data } = await this.client.get<unknown>(`/category/member/all`, { params: serializeCbiDto(options, "generated.dto.CategoryMemberFetchOptions") });
    return deserializeCbiDto<Array<CategoryMember>>(data, "generated.dto.CategoryMember");
  }

  async getAllCategoryStaffs(options?: CategoryFetchOptions): Promise<Array<CategoryStaff>> {
    const { data } = await this.client.get<unknown>(`/category/staff/all`, { params: serializeCbiDto(options, "generated.dto.CategoryFetchOptions") });
    return deserializeCbiDto<Array<CategoryStaff>>(data, "generated.dto.CategoryStaff");
  }

  async getAllAgreementStaffs(options?: AgreementFetchOptions): Promise<Array<AgreementStaff>> {
    const { data } = await this.client.get<unknown>(`/agreement/staff/all`, { params: serializeCbiDto(options, "generated.dto.AgreementFetchOptions") });
    return deserializeCbiDto<Array<AgreementStaff>>(data, "generated.dto.AgreementStaff");
  }

  async getAllAgreementInstanceStaffs(options?: AgreementInstanceFetchOptions): Promise<Array<AgreementInstanceStaff>> {
    const { data } = await this.client.get<unknown>(`/agreementinstance/staff/all`, { params: serializeCbiDto(options, "generated.dto.AgreementInstanceFetchOptions") });
    return deserializeCbiDto<Array<AgreementInstanceStaff>>(data, "generated.dto.AgreementInstanceStaff");
  }

  async getAllAgreementCategoryStaffs(options?: AgreementCategoryFetchOptions): Promise<Array<AgreementCategoryStaff>> {
    const { data } = await this.client.get<unknown>(`/agreementcategory/staff/all`, { params: serializeCbiDto(options, "generated.dto.AgreementCategoryFetchOptions") });
    return deserializeCbiDto<Array<AgreementCategoryStaff>>(data, "generated.dto.AgreementCategoryStaff");
  }

  async getAllCatalogStaffs(options?: CatalogFetchOptions): Promise<Array<CatalogStaff>> {
    const { data } = await this.client.get<unknown>(`/catalog/staff/all`, { params: serializeCbiDto(options, "generated.dto.CatalogFetchOptions") });
    return deserializeCbiDto<Array<CatalogStaff>>(data, "generated.dto.CatalogStaff");
  }

  async getAllProductStaffs(options?: ProductFetchOptions): Promise<Array<ProductStaff>> {
    const { data } = await this.client.get<unknown>(`/product/staff/all`, { params: serializeCbiDto(options, "generated.dto.ProductFetchOptions") });
    return deserializeCbiDto<Array<ProductStaff>>(data, "generated.dto.ProductStaff");
  }

  async getAllCatalogItemStaffs(options?: CatalogItemFetchOptions): Promise<Array<CatalogItemStaff>> {
    const { data } = await this.client.get<unknown>(`/catalogitem/staff/all`, { params: serializeCbiDto(options, "generated.dto.CatalogItemFetchOptions") });
    return deserializeCbiDto<Array<CatalogItemStaff>>(data, "generated.dto.CatalogItemStaff");
  }

  async getAllCatalogItemInstanceStaffs(options?: CatalogItemInstanceFetchOptions): Promise<Array<CatalogItemInstanceStaff>> {
    const { data } = await this.client.get<unknown>(`/catalogiteminstance/staff/all`, { params: serializeCbiDto(options, "generated.dto.CatalogItemInstanceFetchOptions") });
    return deserializeCbiDto<Array<CatalogItemInstanceStaff>>(data, "generated.dto.CatalogItemInstanceStaff");
  }

  async createCategoryStaff(source: CreateCategory): Promise<CategoryStaff> {
    const { data } = await this.client.post<unknown>(`/category/staff`, serializeCbiDto(source, "generated.dto.CreateCategory"));
    return deserializeCbiDto<CategoryStaff>(data, "generated.dto.CategoryStaff");
  }

  async createAgreementStaff(source: CreateAgreement): Promise<AgreementStaff> {
    const { data } = await this.client.post<unknown>(`/agreement/staff`, serializeCbiDto(source, "generated.dto.CreateAgreement"));
    return deserializeCbiDto<AgreementStaff>(data, "generated.dto.AgreementStaff");
  }

  async createAgreementInstanceStaff(source: CreateAgreementInstance): Promise<AgreementInstanceStaff> {
    const { data } = await this.client.post<unknown>(`/agreementinstance/staff`, serializeCbiDto(source, "generated.dto.CreateAgreementInstance"));
    return deserializeCbiDto<AgreementInstanceStaff>(data, "generated.dto.AgreementInstanceStaff");
  }

  async createAgreementCategoryStaff(source: CreateAgreementCategory): Promise<AgreementCategoryStaff> {
    const { data } = await this.client.post<unknown>(`/agreementcategory/staff`, serializeCbiDto(source, "generated.dto.CreateAgreementCategory"));
    return deserializeCbiDto<AgreementCategoryStaff>(data, "generated.dto.AgreementCategoryStaff");
  }

  async createCatalogStaff(source: CreateCatalog): Promise<CatalogStaff> {
    const { data } = await this.client.post<unknown>(`/catalog/staff`, serializeCbiDto(source, "generated.dto.CreateCatalog"));
    return deserializeCbiDto<CatalogStaff>(data, "generated.dto.CatalogStaff");
  }

  async createProductStaff(source: CreateProduct): Promise<ProductStaff> {
    const { data } = await this.client.post<unknown>(`/product/staff`, serializeCbiDto(source, "generated.dto.CreateProduct"));
    return deserializeCbiDto<ProductStaff>(data, "generated.dto.ProductStaff");
  }

  async createCatalogItemStaff(source: CreateCatalogItemStaff): Promise<CatalogItemStaff> {
    const { data } = await this.client.post<unknown>(`/catalogitem/staff`, serializeCbiDto(source, "generated.dto.CreateCatalogItemStaff"));
    return deserializeCbiDto<CatalogItemStaff>(data, "generated.dto.CatalogItemStaff");
  }

  async createCatalogItemInstanceStaff(source: CreateCatalogItemInstance): Promise<CatalogItemInstanceStaff> {
    const { data } = await this.client.post<unknown>(`/catalogiteminstance/staff`, serializeCbiDto(source, "generated.dto.CreateCatalogItemInstance"));
    return deserializeCbiDto<CatalogItemInstanceStaff>(data, "generated.dto.CatalogItemInstanceStaff");
  }

  async createCatalogItemCategoryStaff(source: CreateCatalogItemCategory): Promise<CatalogItemCategoryStaff> {
    const { data } = await this.client.post<unknown>(`/catalogitemcategory/staff`, serializeCbiDto(source, "generated.dto.CreateCatalogItemCategory"));
    return deserializeCbiDto<CatalogItemCategoryStaff>(data, "generated.dto.CatalogItemCategoryStaff");
  }

  async createCatalogItemAgreementStaff(source: CreateCatalogItemAgreement): Promise<CatalogItemAgreementStaff> {
    const { data } = await this.client.post<unknown>(`/catalogitemagreement/staff`, serializeCbiDto(source, "generated.dto.CreateCatalogItemAgreement"));
    return deserializeCbiDto<CatalogItemAgreementStaff>(data, "generated.dto.CatalogItemAgreementStaff");
  }

  async updateCategoryStaff(source: UpdateCategory): Promise<CategoryStaff> {
    const { data } = await this.client.put<unknown>(`/category/staff/${source.assignId}`, serializeCbiDto(source, "generated.dto.UpdateCategory"));
    return deserializeCbiDto<CategoryStaff>(data, "generated.dto.CategoryStaff");
  }

  async updateAgreementStaff(source: UpdateAgreement): Promise<AgreementStaff> {
    const { data } = await this.client.put<unknown>(`/agreement/staff/${source.assignId}`, serializeCbiDto(source, "generated.dto.UpdateAgreement"));
    return deserializeCbiDto<AgreementStaff>(data, "generated.dto.AgreementStaff");
  }

  async updateAgreementInstanceStaff(source: UpdateAgreementInstance): Promise<AgreementInstanceStaff> {
    const { data } = await this.client.put<unknown>(`/agreementinstance/staff/${source.assignId}`, serializeCbiDto(source, "generated.dto.UpdateAgreementInstance"));
    return deserializeCbiDto<AgreementInstanceStaff>(data, "generated.dto.AgreementInstanceStaff");
  }

  async updateAgreementCategoryStaff(source: UpdateAgreementCategory): Promise<AgreementCategoryStaff> {
    const { data } = await this.client.put<unknown>(`/agreementcategory/staff/${source.assignId}`, serializeCbiDto(source, "generated.dto.UpdateAgreementCategory"));
    return deserializeCbiDto<AgreementCategoryStaff>(data, "generated.dto.AgreementCategoryStaff");
  }

  async updateCatalogStaff(source: UpdateCatalog): Promise<CatalogStaff> {
    const { data } = await this.client.put<unknown>(`/catalog/staff/${source.assignId}`, serializeCbiDto(source, "generated.dto.UpdateCatalog"));
    return deserializeCbiDto<CatalogStaff>(data, "generated.dto.CatalogStaff");
  }

  async updateProductStaff(source: UpdateProduct): Promise<ProductStaff> {
    const { data } = await this.client.put<unknown>(`/product/staff/${source.assignId}`, serializeCbiDto(source, "generated.dto.UpdateProduct"));
    return deserializeCbiDto<ProductStaff>(data, "generated.dto.ProductStaff");
  }

  async updateCatalogItemStaff(source: UpdateCatalogItemStaff): Promise<CatalogItemStaff> {
    const { data } = await this.client.put<unknown>(`/catalogitem/staff/${source.assignId}`, serializeCbiDto(source, "generated.dto.UpdateCatalogItemStaff"));
    return deserializeCbiDto<CatalogItemStaff>(data, "generated.dto.CatalogItemStaff");
  }

  async updateCatalogItemInstanceStaff(source: UpdateCatalogItemInstance): Promise<CatalogItemInstanceStaff> {
    const { data } = await this.client.put<unknown>(`/catalogiteminstance/staff/${source.assignId}`, serializeCbiDto(source, "generated.dto.UpdateCatalogItemInstance"));
    return deserializeCbiDto<CatalogItemInstanceStaff>(data, "generated.dto.CatalogItemInstanceStaff");
  }

  async updateCatalogItemCategoryStaff(source: UpdateCatalogItemCategory): Promise<CatalogItemCategoryStaff> {
    const { data } = await this.client.put<unknown>(`/catalogitemcategory/staff/${source.assignId}`, serializeCbiDto(source, "generated.dto.UpdateCatalogItemCategory"));
    return deserializeCbiDto<CatalogItemCategoryStaff>(data, "generated.dto.CatalogItemCategoryStaff");
  }

  async updateCatalogItemAgreementStaff(source: UpdateCatalogItemAgreement): Promise<CatalogItemAgreementStaff> {
    const { data } = await this.client.put<unknown>(`/catalogitemagreement/staff/${source.assignId}`, serializeCbiDto(source, "generated.dto.UpdateCatalogItemAgreement"));
    return deserializeCbiDto<CatalogItemAgreementStaff>(data, "generated.dto.CatalogItemAgreementStaff");
  }

  async deleteCategoryStaff(source: DeleteCategoryStaff): Promise<void> {
    await this.client.delete(`/category/staff/${source.assignId}`);
  }

  async deleteAgreementStaff(source: DeleteAgreementStaff): Promise<void> {
    await this.client.delete(`/agreement/staff/${source.assignId}`);
  }

  async deleteAgreementInstanceStaff(source: DeleteAgreementInstanceStaff): Promise<void> {
    await this.client.delete(`/agreementinstance/staff/${source.assignId}`);
  }

  async deleteAgreementCategoryStaff(source: DeleteAgreementCategoryStaff): Promise<void> {
    await this.client.delete(`/agreementcategory/staff/${source.assignId}`);
  }

  async deleteCatalogStaff(source: DeleteCatalogStaff): Promise<void> {
    await this.client.delete(`/catalog/staff/${source.assignId}`);
  }

  async deleteProductStaff(source: DeleteProductStaff): Promise<void> {
    await this.client.delete(`/product/staff/${source.assignId}`);
  }

  async deleteCatalogItemStaff(source: DeleteCatalogItemStaff): Promise<void> {
    await this.client.delete(`/catalogitem/staff/${source.assignId}`);
  }

  async deleteCatalogItemInstanceStaff(source: DeleteCatalogItemInstanceStaff): Promise<void> {
    await this.client.delete(`/catalogiteminstance/staff/${source.assignId}`);
  }

  async deleteCatalogItemCategoryStaff(source: DeleteCatalogItemCategoryStaff): Promise<void> {
    await this.client.delete(`/catalogitemcategory/staff/${source.assignId}`);
  }

  async deleteCatalogItemAgreementStaff(source: DeleteCatalogItemAgreementStaff): Promise<void> {
    await this.client.delete(`/catalogitemagreement/staff/${source.assignId}`);
  }

  async searchCategoryStaff(params: CategorySearchParams, page: number, perPage: number): Promise<PaginationResult<CategoryStaffSearchResult>> {
    const { data } = await this.client.post<unknown>(`/category/staff/search`, serializeCbiDto(params, "generated.dto.CategorySearchParams"), { params: { page, perPage } });
    let result = data as PaginationResult<CategoryStaffSearchResult>;
    result = { ...result, items: deserializeCbiDto<Array<CategoryStaffSearchResult>>(result.items, "generated.dto.CategoryStaffSearchResult") };
    return result;
  }

  async searchAgreementStaff(params: AgreementSearchParams, page: number, perPage: number): Promise<PaginationResult<AgreementStaffSearchResult>> {
    const { data } = await this.client.post<unknown>(`/agreement/staff/search`, serializeCbiDto(params, "generated.dto.AgreementSearchParams"), { params: { page, perPage } });
    let result = data as PaginationResult<AgreementStaffSearchResult>;
    result = { ...result, items: deserializeCbiDto<Array<AgreementStaffSearchResult>>(result.items, "generated.dto.AgreementStaffSearchResult") };
    return result;
  }

  async searchAgreementInstanceStaff(params: AgreementInstanceSearchParams, page: number, perPage: number): Promise<PaginationResult<AgreementInstanceStaffSearchResult>> {
    const { data } = await this.client.post<unknown>(`/agreementinstance/staff/search`, serializeCbiDto(params, "generated.dto.AgreementInstanceSearchParams"), { params: { page, perPage } });
    let result = data as PaginationResult<AgreementInstanceStaffSearchResult>;
    result = { ...result, items: deserializeCbiDto<Array<AgreementInstanceStaffSearchResult>>(result.items, "generated.dto.AgreementInstanceStaffSearchResult") };
    return result;
  }

  async searchCatalogStaff(params: CatalogSearchParams, page: number, perPage: number): Promise<PaginationResult<CatalogStaffSearchResult>> {
    const { data } = await this.client.post<unknown>(`/catalog/staff/search`, serializeCbiDto(params, "generated.dto.CatalogSearchParams"), { params: { page, perPage } });
    let result = data as PaginationResult<CatalogStaffSearchResult>;
    result = { ...result, items: deserializeCbiDto<Array<CatalogStaffSearchResult>>(result.items, "generated.dto.CatalogStaffSearchResult") };
    return result;
  }

  async searchProductStaff(params: ProductSearchParams, page: number, perPage: number): Promise<PaginationResult<ProductStaffSearchResult>> {
    const { data } = await this.client.post<unknown>(`/product/staff/search`, serializeCbiDto(params, "generated.dto.ProductSearchParams"), { params: { page, perPage } });
    let result = data as PaginationResult<ProductStaffSearchResult>;
    result = { ...result, items: deserializeCbiDto<Array<ProductStaffSearchResult>>(result.items, "generated.dto.ProductStaffSearchResult") };
    return result;
  }

  async searchCatalogItemStaff(params: CatalogItemSearchParams, page: number, perPage: number): Promise<PaginationResult<CatalogItemStaffSearchResult>> {
    const { data } = await this.client.post<unknown>(`/catalogitem/staff/search`, serializeCbiDto(params, "generated.dto.CatalogItemSearchParams"), { params: { page, perPage } });
    let result = data as PaginationResult<CatalogItemStaffSearchResult>;
    result = { ...result, items: deserializeCbiDto<Array<CatalogItemStaffSearchResult>>(result.items, "generated.dto.CatalogItemStaffSearchResult") };
    return result;
  }

  async searchCategoryStaffFull(params: CategorySearchParams, page: number, perPage: number, options?: CategoryFetchOptions): Promise<PaginationResult<CategoryStaff>> {
    const { data } = await this.client.post<unknown>(`/category/staff/search/full`, serializeCbiDto(params, "generated.dto.CategorySearchParams"), { params: { ...(serializeCbiDto(options, "generated.dto.CategoryFetchOptions") as Record<string, unknown>), ...{ page, perPage } } });
    let result = data as PaginationResult<CategoryStaff>;
    result = { ...result, items: deserializeCbiDto<Array<CategoryStaff>>(result.items, "generated.dto.CategoryStaff") };
    return result;
  }

  async searchAgreementStaffFull(params: AgreementSearchParams, page: number, perPage: number, options?: AgreementFetchOptions): Promise<PaginationResult<AgreementStaff>> {
    const { data } = await this.client.post<unknown>(`/agreement/staff/search/full`, serializeCbiDto(params, "generated.dto.AgreementSearchParams"), { params: { ...(serializeCbiDto(options, "generated.dto.AgreementFetchOptions") as Record<string, unknown>), ...{ page, perPage } } });
    let result = data as PaginationResult<AgreementStaff>;
    result = { ...result, items: deserializeCbiDto<Array<AgreementStaff>>(result.items, "generated.dto.AgreementStaff") };
    return result;
  }

  async searchAgreementInstanceStaffFull(params: AgreementInstanceSearchParams, page: number, perPage: number, options?: AgreementInstanceFetchOptions): Promise<PaginationResult<AgreementInstanceStaff>> {
    const { data } = await this.client.post<unknown>(`/agreementinstance/staff/search/full`, serializeCbiDto(params, "generated.dto.AgreementInstanceSearchParams"), { params: { ...(serializeCbiDto(options, "generated.dto.AgreementInstanceFetchOptions") as Record<string, unknown>), ...{ page, perPage } } });
    let result = data as PaginationResult<AgreementInstanceStaff>;
    result = { ...result, items: deserializeCbiDto<Array<AgreementInstanceStaff>>(result.items, "generated.dto.AgreementInstanceStaff") };
    return result;
  }

  async searchCatalogStaffFull(params: CatalogSearchParams, page: number, perPage: number, options?: CatalogFetchOptions): Promise<PaginationResult<CatalogStaff>> {
    const { data } = await this.client.post<unknown>(`/catalog/staff/search/full`, serializeCbiDto(params, "generated.dto.CatalogSearchParams"), { params: { ...(serializeCbiDto(options, "generated.dto.CatalogFetchOptions") as Record<string, unknown>), ...{ page, perPage } } });
    let result = data as PaginationResult<CatalogStaff>;
    result = { ...result, items: deserializeCbiDto<Array<CatalogStaff>>(result.items, "generated.dto.CatalogStaff") };
    return result;
  }

  async searchProductStaffFull(params: ProductSearchParams, page: number, perPage: number, options?: ProductFetchOptions): Promise<PaginationResult<ProductStaff>> {
    const { data } = await this.client.post<unknown>(`/product/staff/search/full`, serializeCbiDto(params, "generated.dto.ProductSearchParams"), { params: { ...(serializeCbiDto(options, "generated.dto.ProductFetchOptions") as Record<string, unknown>), ...{ page, perPage } } });
    let result = data as PaginationResult<ProductStaff>;
    result = { ...result, items: deserializeCbiDto<Array<ProductStaff>>(result.items, "generated.dto.ProductStaff") };
    return result;
  }

  async searchCatalogItemStaffFull(params: CatalogItemSearchParams, page: number, perPage: number, options?: CatalogItemFetchOptions): Promise<PaginationResult<CatalogItemStaff>> {
    const { data } = await this.client.post<unknown>(`/catalogitem/staff/search/full`, serializeCbiDto(params, "generated.dto.CatalogItemSearchParams"), { params: { ...(serializeCbiDto(options, "generated.dto.CatalogItemFetchOptions") as Record<string, unknown>), ...{ page, perPage } } });
    let result = data as PaginationResult<CatalogItemStaff>;
    result = { ...result, items: deserializeCbiDto<Array<CatalogItemStaff>>(result.items, "generated.dto.CatalogItemStaff") };
    return result;
  }

  async getCategoryStaffItemCategories(id: number, options?: CatalogItemCategoryFetchOptions): Promise<Array<CatalogItemCategoryStaff>> {
    const { data } = await this.client.get<unknown>(`/category/staff/${id}/itemcategories`, { params: serializeCbiDto(options, "generated.dto.CatalogItemCategoryFetchOptions") });
    return deserializeCbiDto<Array<CatalogItemCategoryStaff>>(data, "generated.dto.CatalogItemCategoryStaff");
  }

  async getCategoryStaffAgreementCategories(id: number, options?: AgreementCategoryFetchOptions): Promise<Array<AgreementCategoryStaff>> {
    const { data } = await this.client.get<unknown>(`/category/staff/${id}/agreementcategories`, { params: serializeCbiDto(options, "generated.dto.AgreementCategoryFetchOptions") });
    return deserializeCbiDto<Array<AgreementCategoryStaff>>(data, "generated.dto.AgreementCategoryStaff");
  }

  async getAgreementStaffCatalogId(id: number, options?: CatalogFetchOptions): Promise<CatalogStaff> {
    const { data } = await this.client.get<unknown>(`/agreement/staff/${id}/catalogid`, { params: serializeCbiDto(options, "generated.dto.CatalogFetchOptions") });
    return deserializeCbiDto<CatalogStaff>(data, "generated.dto.CatalogStaff");
  }

  async getAgreementStaffItems(id: number, options?: CatalogItemAgreementFetchOptions): Promise<Array<CatalogItemAgreementStaff>> {
    const { data } = await this.client.get<unknown>(`/agreement/staff/${id}/items`, { params: serializeCbiDto(options, "generated.dto.CatalogItemAgreementFetchOptions") });
    return deserializeCbiDto<Array<CatalogItemAgreementStaff>>(data, "generated.dto.CatalogItemAgreementStaff");
  }

  async getAgreementStaffAgreementInstances(id: number, options?: AgreementInstanceFetchOptions): Promise<Array<AgreementInstanceStaff>> {
    const { data } = await this.client.get<unknown>(`/agreement/staff/${id}/agreementinstances`, { params: serializeCbiDto(options, "generated.dto.AgreementInstanceFetchOptions") });
    return deserializeCbiDto<Array<AgreementInstanceStaff>>(data, "generated.dto.AgreementInstanceStaff");
  }

  async getAgreementStaffAgreementCategories(id: number, options?: AgreementCategoryFetchOptions): Promise<Array<AgreementCategoryStaff>> {
    const { data } = await this.client.get<unknown>(`/agreement/staff/${id}/agreementcategories`, { params: serializeCbiDto(options, "generated.dto.AgreementCategoryFetchOptions") });
    return deserializeCbiDto<Array<AgreementCategoryStaff>>(data, "generated.dto.AgreementCategoryStaff");
  }

  async getAgreementInstanceStaffAgreementId(id: number, options?: AgreementFetchOptions): Promise<AgreementStaff> {
    const { data } = await this.client.get<unknown>(`/agreementinstance/staff/${id}/agreementid`, { params: serializeCbiDto(options, "generated.dto.AgreementFetchOptions") });
    return deserializeCbiDto<AgreementStaff>(data, "generated.dto.AgreementStaff");
  }

  async getAgreementCategoryStaffAgreementId(id: number, options?: AgreementFetchOptions): Promise<AgreementStaff | null> {
    const { data } = await this.client.get<unknown>(`/agreementcategory/staff/${id}/agreementid`, { params: serializeCbiDto(options, "generated.dto.AgreementFetchOptions") });
    return deserializeCbiDto<AgreementStaff | null>(data, "generated.dto.AgreementStaff");
  }

  async getAgreementCategoryStaffCategoryId(id: number, options?: CategoryFetchOptions): Promise<CategoryStaff> {
    const { data } = await this.client.get<unknown>(`/agreementcategory/staff/${id}/categoryid`, { params: serializeCbiDto(options, "generated.dto.CategoryFetchOptions") });
    return deserializeCbiDto<CategoryStaff>(data, "generated.dto.CategoryStaff");
  }

  async getCatalogStaffItems(id: number, options?: CatalogItemFetchOptions): Promise<Array<CatalogItemStaff>> {
    const { data } = await this.client.get<unknown>(`/catalog/staff/${id}/items`, { params: serializeCbiDto(options, "generated.dto.CatalogItemFetchOptions") });
    return deserializeCbiDto<Array<CatalogItemStaff>>(data, "generated.dto.CatalogItemStaff");
  }

  async getCatalogStaffProducts(id: number, options?: ProductFetchOptions): Promise<Array<ProductStaff>> {
    const { data } = await this.client.get<unknown>(`/catalog/staff/${id}/products`, { params: serializeCbiDto(options, "generated.dto.ProductFetchOptions") });
    return deserializeCbiDto<Array<ProductStaff>>(data, "generated.dto.ProductStaff");
  }

  async getCatalogStaffAgreements(id: number, options?: AgreementFetchOptions): Promise<Array<AgreementStaff>> {
    const { data } = await this.client.get<unknown>(`/catalog/staff/${id}/agreements`, { params: serializeCbiDto(options, "generated.dto.AgreementFetchOptions") });
    return deserializeCbiDto<Array<AgreementStaff>>(data, "generated.dto.AgreementStaff");
  }

  async getCatalogStaffTaxes(id: number, options?: CatalogTaxFetchOptions): Promise<Array<CatalogTaxStaff>> {
    const { data } = await this.client.get<unknown>(`/catalog/staff/${id}/taxes`, { params: serializeCbiDto(options, "generated.dto.CatalogTaxFetchOptions") });
    return deserializeCbiDto<Array<CatalogTaxStaff>>(data, "generated.dto.CatalogTaxStaff");
  }

  async getCatalogStaffDiscounts(id: number, options?: CatalogDiscountFetchOptions): Promise<Array<CatalogDiscountStaff>> {
    const { data } = await this.client.get<unknown>(`/catalog/staff/${id}/discounts`, { params: serializeCbiDto(options, "generated.dto.CatalogDiscountFetchOptions") });
    return deserializeCbiDto<Array<CatalogDiscountStaff>>(data, "generated.dto.CatalogDiscountStaff");
  }

  async getProductStaffCatalogId(id: number, options?: CatalogFetchOptions): Promise<CatalogStaff> {
    const { data } = await this.client.get<unknown>(`/product/staff/${id}/catalogid`, { params: serializeCbiDto(options, "generated.dto.CatalogFetchOptions") });
    return deserializeCbiDto<CatalogStaff>(data, "generated.dto.CatalogStaff");
  }

  async getProductStaffItems(id: number, options?: CatalogItemFetchOptions): Promise<Array<CatalogItemStaff>> {
    const { data } = await this.client.get<unknown>(`/product/staff/${id}/items`, { params: serializeCbiDto(options, "generated.dto.CatalogItemFetchOptions") });
    return deserializeCbiDto<Array<CatalogItemStaff>>(data, "generated.dto.CatalogItemStaff");
  }

  async getCatalogItemStaffCatalogId(id: number, options?: CatalogFetchOptions): Promise<CatalogStaff | null> {
    const { data } = await this.client.get<unknown>(`/catalogitem/staff/${id}/catalogid`, { params: serializeCbiDto(options, "generated.dto.CatalogFetchOptions") });
    return deserializeCbiDto<CatalogStaff | null>(data, "generated.dto.CatalogStaff");
  }

  async getCatalogItemStaffItemInstances(id: number, options?: CatalogItemInstanceFetchOptions): Promise<Array<CatalogItemInstanceStaff>> {
    const { data } = await this.client.get<unknown>(`/catalogitem/staff/${id}/iteminstances`, { params: serializeCbiDto(options, "generated.dto.CatalogItemInstanceFetchOptions") });
    return deserializeCbiDto<Array<CatalogItemInstanceStaff>>(data, "generated.dto.CatalogItemInstanceStaff");
  }

  async getCatalogItemStaffCategories(id: number, options?: CatalogItemCategoryFetchOptions): Promise<Array<CatalogItemCategoryStaff>> {
    const { data } = await this.client.get<unknown>(`/catalogitem/staff/${id}/categories`, { params: serializeCbiDto(options, "generated.dto.CatalogItemCategoryFetchOptions") });
    return deserializeCbiDto<Array<CatalogItemCategoryStaff>>(data, "generated.dto.CatalogItemCategoryStaff");
  }

  async getCatalogItemStaffAgreements(id: number, options?: CatalogItemAgreementFetchOptions): Promise<Array<CatalogItemAgreementStaff>> {
    const { data } = await this.client.get<unknown>(`/catalogitem/staff/${id}/agreements`, { params: serializeCbiDto(options, "generated.dto.CatalogItemAgreementFetchOptions") });
    return deserializeCbiDto<Array<CatalogItemAgreementStaff>>(data, "generated.dto.CatalogItemAgreementStaff");
  }

  async getCatalogItemStaffTaxes(id: number, options?: CatalogItemTaxFetchOptions): Promise<Array<CatalogItemTaxStaff>> {
    const { data } = await this.client.get<unknown>(`/catalogitem/staff/${id}/taxes`, { params: serializeCbiDto(options, "generated.dto.CatalogItemTaxFetchOptions") });
    return deserializeCbiDto<Array<CatalogItemTaxStaff>>(data, "generated.dto.CatalogItemTaxStaff");
  }

  async getCatalogItemStaffItemDiscounts(id: number, options?: CatalogItemDiscountFetchOptions): Promise<Array<CatalogItemDiscountStaff>> {
    const { data } = await this.client.get<unknown>(`/catalogitem/staff/${id}/itemdiscounts`, { params: serializeCbiDto(options, "generated.dto.CatalogItemDiscountFetchOptions") });
    return deserializeCbiDto<Array<CatalogItemDiscountStaff>>(data, "generated.dto.CatalogItemDiscountStaff");
  }

  async getCatalogItemStaffProductId(id: number, options?: ProductFetchOptions): Promise<ProductStaff | null> {
    const { data } = await this.client.get<unknown>(`/catalogitem/staff/${id}/productid`, { params: serializeCbiDto(options, "generated.dto.ProductFetchOptions") });
    return deserializeCbiDto<ProductStaff | null>(data, "generated.dto.ProductStaff");
  }

  async getCatalogItemInstanceStaffItemId(id: number, options?: CatalogItemFetchOptions): Promise<CatalogItemStaff> {
    const { data } = await this.client.get<unknown>(`/catalogiteminstance/staff/${id}/itemid`, { params: serializeCbiDto(options, "generated.dto.CatalogItemFetchOptions") });
    return deserializeCbiDto<CatalogItemStaff>(data, "generated.dto.CatalogItemStaff");
  }

  async getCatalogItemInstanceStaffStockChanges(id: number, options?: StockChangeFetchOptions): Promise<Array<StockChangeStaff>> {
    const { data } = await this.client.get<unknown>(`/catalogiteminstance/staff/${id}/stockchanges`, { params: serializeCbiDto(options, "generated.dto.StockChangeFetchOptions") });
    return deserializeCbiDto<Array<StockChangeStaff>>(data, "generated.dto.StockChangeStaff");
  }

  async getCatalogItemCategoryStaffItem(id: number, options?: CatalogItemFetchOptions): Promise<CatalogItemStaff> {
    const { data } = await this.client.get<unknown>(`/catalogitemcategory/staff/${id}/item`, { params: serializeCbiDto(options, "generated.dto.CatalogItemFetchOptions") });
    return deserializeCbiDto<CatalogItemStaff>(data, "generated.dto.CatalogItemStaff");
  }

  async getCatalogItemCategoryStaffCategoryId(id: number, options?: CategoryFetchOptions): Promise<CategoryStaff> {
    const { data } = await this.client.get<unknown>(`/catalogitemcategory/staff/${id}/categoryid`, { params: serializeCbiDto(options, "generated.dto.CategoryFetchOptions") });
    return deserializeCbiDto<CategoryStaff>(data, "generated.dto.CategoryStaff");
  }

  async getCatalogItemAgreementStaffItemId(id: number, options?: CatalogItemFetchOptions): Promise<CatalogItemStaff> {
    const { data } = await this.client.get<unknown>(`/catalogitemagreement/staff/${id}/itemid`, { params: serializeCbiDto(options, "generated.dto.CatalogItemFetchOptions") });
    return deserializeCbiDto<CatalogItemStaff>(data, "generated.dto.CatalogItemStaff");
  }

  async getCatalogItemAgreementStaffAgreementId(id: number, options?: AgreementFetchOptions): Promise<AgreementStaff> {
    const { data } = await this.client.get<unknown>(`/catalogitemagreement/staff/${id}/agreementid`, { params: serializeCbiDto(options, "generated.dto.AgreementFetchOptions") });
    return deserializeCbiDto<AgreementStaff>(data, "generated.dto.AgreementStaff");
  }

  async addCatalogItemStaffCategories(parent: CatalogItemRef, values: Array<CatalogItemCategoriesStaffAssignmentInput>): Promise<Array<CatalogItemCategoryStaff>> {
    const { data } = await this.client.post<unknown>(`/catalogitem/staff/${parent.assignId}/categories/add`, serializeCbiDto(values, "generated.dto.CatalogItemCategoriesStaffAssignmentInput"));
    return deserializeCbiDto<Array<CatalogItemCategoryStaff>>(data, "generated.dto.CatalogItemCategoryStaff");
  }

  async putCatalogItemStaffCategories(parent: CatalogItemRef, values: Array<CatalogItemCategoriesStaffAssignmentInput>): Promise<Array<CatalogItemCategoryStaff>> {
    const { data } = await this.client.post<unknown>(`/catalogitem/staff/${parent.assignId}/categories/put`, serializeCbiDto(values, "generated.dto.CatalogItemCategoriesStaffAssignmentInput"));
    return deserializeCbiDto<Array<CatalogItemCategoryStaff>>(data, "generated.dto.CatalogItemCategoryStaff");
  }

  async removeCatalogItemStaffCategories(parent: CatalogItemRef, values: Array<CategoryRef>): Promise<Array<CatalogItemCategoryStaff>> {
    const { data } = await this.client.post<unknown>(`/catalogitem/staff/${parent.assignId}/categories/remove`, serializeCbiDto(values, "generated.dto.CategoryRef"));
    return deserializeCbiDto<Array<CatalogItemCategoryStaff>>(data, "generated.dto.CatalogItemCategoryStaff");
  }

  async clearCatalogItemStaffCategories(parent: CatalogItemRef): Promise<Array<CatalogItemCategoryStaff>> {
    const { data } = await this.client.post<unknown>(`/catalogitem/staff/${parent.assignId}/categories/clear`);
    return deserializeCbiDto<Array<CatalogItemCategoryStaff>>(data, "generated.dto.CatalogItemCategoryStaff");
  }

  async addCatalogItemStaffAgreements(parent: CatalogItemRef, values: Array<CatalogItemAgreementsStaffAssignmentInput>): Promise<Array<CatalogItemAgreementStaff>> {
    const { data } = await this.client.post<unknown>(`/catalogitem/staff/${parent.assignId}/agreements/add`, serializeCbiDto(values, "generated.dto.CatalogItemAgreementsStaffAssignmentInput"));
    return deserializeCbiDto<Array<CatalogItemAgreementStaff>>(data, "generated.dto.CatalogItemAgreementStaff");
  }

  async putCatalogItemStaffAgreements(parent: CatalogItemRef, values: Array<CatalogItemAgreementsStaffAssignmentInput>): Promise<Array<CatalogItemAgreementStaff>> {
    const { data } = await this.client.post<unknown>(`/catalogitem/staff/${parent.assignId}/agreements/put`, serializeCbiDto(values, "generated.dto.CatalogItemAgreementsStaffAssignmentInput"));
    return deserializeCbiDto<Array<CatalogItemAgreementStaff>>(data, "generated.dto.CatalogItemAgreementStaff");
  }

  async removeCatalogItemStaffAgreements(parent: CatalogItemRef, values: Array<AgreementRef>): Promise<Array<CatalogItemAgreementStaff>> {
    const { data } = await this.client.post<unknown>(`/catalogitem/staff/${parent.assignId}/agreements/remove`, serializeCbiDto(values, "generated.dto.AgreementRef"));
    return deserializeCbiDto<Array<CatalogItemAgreementStaff>>(data, "generated.dto.CatalogItemAgreementStaff");
  }

  async clearCatalogItemStaffAgreements(parent: CatalogItemRef): Promise<Array<CatalogItemAgreementStaff>> {
    const { data } = await this.client.post<unknown>(`/catalogitem/staff/${parent.assignId}/agreements/clear`);
    return deserializeCbiDto<Array<CatalogItemAgreementStaff>>(data, "generated.dto.CatalogItemAgreementStaff");
  }

  async addCatalogItemStaffTaxes(parent: CatalogItemRef, values: Array<CatalogItemTaxesStaffAssignmentInput>): Promise<Array<CatalogItemTaxStaff>> {
    const { data } = await this.client.post<unknown>(`/catalogitem/staff/${parent.assignId}/taxes/add`, serializeCbiDto(values, "generated.dto.CatalogItemTaxesStaffAssignmentInput"));
    return deserializeCbiDto<Array<CatalogItemTaxStaff>>(data, "generated.dto.CatalogItemTaxStaff");
  }

  async putCatalogItemStaffTaxes(parent: CatalogItemRef, values: Array<CatalogItemTaxesStaffAssignmentInput>): Promise<Array<CatalogItemTaxStaff>> {
    const { data } = await this.client.post<unknown>(`/catalogitem/staff/${parent.assignId}/taxes/put`, serializeCbiDto(values, "generated.dto.CatalogItemTaxesStaffAssignmentInput"));
    return deserializeCbiDto<Array<CatalogItemTaxStaff>>(data, "generated.dto.CatalogItemTaxStaff");
  }

  async removeCatalogItemStaffTaxes(parent: CatalogItemRef, values: Array<CatalogTaxRef>): Promise<Array<CatalogItemTaxStaff>> {
    const { data } = await this.client.post<unknown>(`/catalogitem/staff/${parent.assignId}/taxes/remove`, serializeCbiDto(values, "generated.dto.CatalogTaxRef"));
    return deserializeCbiDto<Array<CatalogItemTaxStaff>>(data, "generated.dto.CatalogItemTaxStaff");
  }

  async clearCatalogItemStaffTaxes(parent: CatalogItemRef): Promise<Array<CatalogItemTaxStaff>> {
    const { data } = await this.client.post<unknown>(`/catalogitem/staff/${parent.assignId}/taxes/clear`);
    return deserializeCbiDto<Array<CatalogItemTaxStaff>>(data, "generated.dto.CatalogItemTaxStaff");
  }

  async addCatalogItemStaffItemDiscounts(parent: CatalogItemRef, values: Array<CatalogItemItemDiscountsStaffAssignmentInput>): Promise<Array<CatalogItemDiscountStaff>> {
    const { data } = await this.client.post<unknown>(`/catalogitem/staff/${parent.assignId}/itemdiscounts/add`, serializeCbiDto(values, "generated.dto.CatalogItemItemDiscountsStaffAssignmentInput"));
    return deserializeCbiDto<Array<CatalogItemDiscountStaff>>(data, "generated.dto.CatalogItemDiscountStaff");
  }

  async putCatalogItemStaffItemDiscounts(parent: CatalogItemRef, values: Array<CatalogItemItemDiscountsStaffAssignmentInput>): Promise<Array<CatalogItemDiscountStaff>> {
    const { data } = await this.client.post<unknown>(`/catalogitem/staff/${parent.assignId}/itemdiscounts/put`, serializeCbiDto(values, "generated.dto.CatalogItemItemDiscountsStaffAssignmentInput"));
    return deserializeCbiDto<Array<CatalogItemDiscountStaff>>(data, "generated.dto.CatalogItemDiscountStaff");
  }

  async removeCatalogItemStaffItemDiscounts(parent: CatalogItemRef, values: Array<CatalogDiscountRef>): Promise<Array<CatalogItemDiscountStaff>> {
    const { data } = await this.client.post<unknown>(`/catalogitem/staff/${parent.assignId}/itemdiscounts/remove`, serializeCbiDto(values, "generated.dto.CatalogDiscountRef"));
    return deserializeCbiDto<Array<CatalogItemDiscountStaff>>(data, "generated.dto.CatalogItemDiscountStaff");
  }

  async clearCatalogItemStaffItemDiscounts(parent: CatalogItemRef): Promise<Array<CatalogItemDiscountStaff>> {
    const { data } = await this.client.post<unknown>(`/catalogitem/staff/${parent.assignId}/itemdiscounts/clear`);
    return deserializeCbiDto<Array<CatalogItemDiscountStaff>>(data, "generated.dto.CatalogItemDiscountStaff");
  }

  async browseItemsPublic(): Promise<Array<CatalogItemInstanceMember>> {
    const { data } = await this.client.get<unknown>(`/catalog/public/browseitems`);
    let result = data as Array<CatalogItemInstanceMember>;
    result = deserializeCbiDto<Array<CatalogItemInstanceMember>>(result, "generated.dto.CatalogItemInstanceMember");
    return result;
  }

  async changeAgreementVersionStaff(request: AgreementVersionChangeRequest): Promise<AgreementInstance> {
    const { data } = await this.client.post<unknown>(`/catalog/staff/changeagreementversion`, serializeCbiDto(request, "generated.dto.AgreementVersionChangeRequest"));
    let result = data as AgreementInstance;
    result = deserializeCbiDto<AgreementInstance>(result, "generated.dto.AgreementInstance");
    return result;
  }

  async changeItemPriceStaff(request: CatalogPriceChangeRequest): Promise<CatalogItemInstance> {
    const { data } = await this.client.post<unknown>(`/catalog/staff/changeitemprice`, serializeCbiDto(request, "generated.dto.CatalogPriceChangeRequest"));
    let result = data as CatalogItemInstance;
    result = deserializeCbiDto<CatalogItemInstance>(result, "generated.dto.CatalogItemInstance");
    return result;
  }

  async onSaleItemInstancesStaff(): Promise<Array<CatalogItemInstanceStaff>> {
    const { data } = await this.client.get<unknown>(`/catalog/staff/onsaleiteminstances`);
    let result = data as Array<CatalogItemInstanceStaff>;
    result = deserializeCbiDto<Array<CatalogItemInstanceStaff>>(result, "generated.dto.CatalogItemInstanceStaff");
    return result;
  }

  async onSaleItemInstancesMember(): Promise<Array<CatalogItemInstanceMember>> {
    const { data } = await this.client.get<unknown>(`/catalog/member/onsaleiteminstances`);
    let result = data as Array<CatalogItemInstanceMember>;
    result = deserializeCbiDto<Array<CatalogItemInstanceMember>>(result, "generated.dto.CatalogItemInstanceMember");
    return result;
  }

  async saveCatalogItemStaff(request: SaveCatalogItemRequest): Promise<CatalogItemStaff> {
    const { data } = await this.client.post<unknown>(`/catalog/staff/savecatalogitem`, serializeCbiDto(request, "generated.dto.SaveCatalogItemRequest"));
    let result = data as CatalogItemStaff;
    result = deserializeCbiDto<CatalogItemStaff>(result, "generated.dto.CatalogItemStaff");
    return result;
  }
}

export const useCatalogApi = (): CatalogApi => new CatalogApiClient();
